// ════════════════════════════════════════════════════════════════════
// Glass-i — Sales Tax Invoice activity timeline (Phase 1B).
// Reads GET /api/erp/sales/invoices/:id/events and renders a compact,
// newest-first history (actor · when · event · safe context). Read-only.
// ════════════════════════════════════════════════════════════════════

import { useEffect, useState } from 'react';
import { Timeline, Spin, Empty, Alert, Tag } from 'antd';
import { invoiceApi } from './invoiceApi';
import { eventTypeLabel, formatMoney } from './invoiceHelpers';

function fmtWhen(ts) {
  if (!ts) return '';
  try { return new Date(ts).toLocaleString('en-IN'); } catch (_e) { return String(ts); }
}

// Render the safe metadata as short human context (never raw JSON dumps).
function contextLine(ev) {
  const m = ev.metadata || {};
  const bits = [];
  if (m.proformaNo) bits.push(`Proforma ${m.proformaNo}`);
  if (m.reason) bits.push(`Reason: ${m.reason}`);
  if (Array.isArray(m.changedFields) && m.changedFields.length) bits.push(`Changed: ${m.changedFields.join(', ')}`);
  if (m.grandTotalTo != null && m.grandTotalFrom != null && Number(m.grandTotalFrom) !== Number(m.grandTotalTo)) {
    bits.push(`Total ${formatMoney(m.grandTotalFrom)} → ${formatMoney(m.grandTotalTo)}`);
  } else if (m.grandTotal != null && !bits.length) {
    bits.push(`Total ${formatMoney(m.grandTotal)}`);
  }
  return bits.join(' · ');
}

const DOT_COLOR = {
  CREATED: 'blue', IMPORTED_FROM_PROFORMA: 'geekblue', UPDATED: 'gold',
  DRAFT_DISCARDED: 'red', POSTED: 'green', CANCELLED: 'red'
};

export default function InvoiceEventsTimeline({ invoiceId }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!invoiceId) return undefined;
    let cancelled = false;
    setLoading(true); setError(null);
    invoiceApi.events(invoiceId)
      .then((rows) => { if (!cancelled) setEvents(Array.isArray(rows) ? rows : []); })
      .catch((e) => { if (!cancelled) setError((e && e.message) || 'Failed to load activity.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [invoiceId]);

  if (loading) return <div className="py-6 text-center"><Spin /></div>;
  if (error) return <Alert type="error" showIcon message={error} />;
  if (!events.length) return <Empty description="No activity yet" image={Empty.PRESENTED_IMAGE_SIMPLE} />;

  return (
    <Timeline
      items={events.map((ev) => {
        const ctx = contextLine(ev);
        return {
          color: DOT_COLOR[String(ev.eventType).toUpperCase()] || 'gray',
          children: (
            <div>
              <div className="flex items-center gap-2">
                <span className="font-medium text-slate-700">{eventTypeLabel(ev.eventType)}</span>
                {ev.fromStatus && ev.toStatus && ev.fromStatus !== ev.toStatus ? (
                  <Tag className="!m-0">{ev.fromStatus} → {ev.toStatus}</Tag>
                ) : null}
              </div>
              <div className="text-xs text-slate-500">
                {fmtWhen(ev.createdAt)}{ev.actorName ? ` · ${ev.actorName}` : ''}
              </div>
              {ctx ? <div className="text-xs text-slate-600 mt-0.5">{ctx}</div> : null}
            </div>
          )
        };
      })}
    />
  );
}
